/**
 * App.js - Course-only build
 *
 * Responsibilities:
 *  1. Start a global GPS watch and broadcast position as a 'gpsUpdate' CustomEvent
 *     so that any component (CoursePage, UnifiedCourseMap, …) can listen without
 *     each owning its own navigator.geolocation.watchPosition.
 *  2. Wrap the app with ThemeProvider and route everything to CoursePage.
 *
 * No authentication, no backend, no events / races / profile / helm.
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './styles/themes';
import CoursePage from './pages/CoursePage';
import ManualPage from './pages/ManualPage';

function App() {
  // ── Global GPS monitor ────────────────────────────────────────────────────
  // Dispatches window CustomEvent 'gpsUpdate' with GPS + orientation data.
  // Components that need the position just add an event listener.
  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn('[App] Geolocation not supported by this browser');
      return;
    }

    const dispatch = (detail) =>
      window.dispatchEvent(new CustomEvent('gpsUpdate', { detail }));

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        dispatch({
          source:    'REAL',
          lat:       pos.coords.latitude.toString(),
          lng:       pos.coords.longitude.toString(),
          speed:     pos.coords.speed,
          heading:   pos.coords.heading,
          accuracy:  pos.coords.accuracy,
          timestamp: new Date()
        });
      },
      (err) => {
        console.warn('[App] GPS error:', err.message);
        dispatch({ source: 'ERROR', lat: null, lng: null, error: err.message });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 1000 }
    );

    // Device orientation (compass heading) – best-effort
    let orientationHandler = null;
    const setupOrientation = () => {
      orientationHandler = (e) => {
        window.dispatchEvent(new CustomEvent('orientationUpdate', {
          detail: { alpha: e.alpha, beta: e.beta, gamma: e.gamma }
        }));
      };
      window.addEventListener('deviceorientation', orientationHandler, false);
    };

    if (typeof DeviceOrientationEvent !== 'undefined') {
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        // iOS 13+ requires a user gesture; defer until first interaction
        const grantOrientation = () => {
          DeviceOrientationEvent.requestPermission()
            .then(state => { if (state === 'granted') setupOrientation(); })
            .catch(() => {});
          window.removeEventListener('click', grantOrientation);
        };
        window.addEventListener('click', grantOrientation, { once: true });
      } else {
        setupOrientation();
      }
    }

    return () => {
      navigator.geolocation.clearWatch(watchId);
      if (orientationHandler) {
        window.removeEventListener('deviceorientation', orientationHandler, false);
      }
    };
  }, []);

  // ── Routing ───────────────────────────────────────────────────────────────
  return (
    <ThemeProvider>
      <Router>
        <Routes>
          {/* All paths → CoursePage (no nested routes needed) */}
          <Route path="/manual" element={<ManualPage />} />
          <Route path="/*" element={<CoursePage />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
